import React, { useState } from 'react';
import { Upload, Button, Table, message, Card, Alert, Space, Radio } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

function OrderImport() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [checkType, setCheckType] = useState('account');
  const [duplicateData, setDuplicateData] = useState([]);
  const [checking, setChecking] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // 预览并查重
  const handlePreview = async () => {
    if (fileList.length === 0) {
      message.warning('请先选择Excel文件');
      return;
    }

    setChecking(true);
    setDuplicateData([]);
    setPreviewData(null);

    const formData = new FormData();
    formData.append('file', fileList[0]);
    formData.append('check_type', checkType);

    try {
      const res = await api.post('/orders/batch/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.code === 200) {
        setPreviewData(res.data);
        if (res.data.missing_team_count > 0) {
          message.warning(`有 ${res.data.missing_team_count} 行未填写小组，请补充后再导入`);
        } else if (res.data.duplicates && res.data.duplicates.length > 0) {
          message.warning(`发现 ${res.data.duplicates.length} 条重复记录`);
        } else {
          message.success('预览完成，未发现重复数据');
        }
      }
    } catch (err) {
      // 错误已在拦截器中处理
    }
    setChecking(false);
  };

  const handleImport = async (skipDuplicates = false) => {
    if (fileList.length === 0) {
      message.warning('请先选择Excel文件');
      return;
    }
    if (previewData && previewData.missing_team_count > 0) {
      message.warning(`有 ${previewData.missing_team_count} 行未填写小组，请补充后再导入`);
      return;
    }

    setImporting(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', fileList[0]);
    formData.append('check_type', checkType);
    if (skipDuplicates) {
      formData.append('skip_duplicates', 'true');
    }

    try {
      const res = await api.post('/orders/batch/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.code === 200) {
        setResult(res.data);
        message.success(res.message);
      }
    } catch (err) {
      // 错误已在拦截器中处理
    }
    setImporting(false);
  };

  const errorColumns = [
    { title: '行号', dataIndex: 'row', width: 80 },
    { title: '错误信息', dataIndex: 'error' }
  ];

  const duplicateColumns = [
    { title: '行号', dataIndex: 'row', width: 80 },
    { title: '抖音号', dataIndex: 'account_name' },
    { title: '涨粉方式', dataIndex: 'growth_method' },
    { title: '主体', dataIndex: 'supplier_name', render: v => v || '-' },
    { title: '小组', dataIndex: 'team', render: v => v || '-' },
    { title: '已存在订单ID', dataIndex: 'existing_id', width: 100 },
    { title: '类型', dataIndex: 'type', width: 80, render: v => v === 'update' ? '更新' : '新增' }
  ];

  const previewColumns = [
    { title: '行号', dataIndex: 'row', width: 60 },
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '抖音号', dataIndex: 'account_name' },
    { title: '主体', dataIndex: 'supplier_name' },
    { title: '涨粉方式', dataIndex: 'growth_method' },
    { title: '平台', dataIndex: 'platform', width: 80 },
    { title: '小组', dataIndex: 'team', width: 120, render: (v, r) => r.missing_team ? <span style={{ color: '#ff4d4f' }}>未填写</span> : (v || '-') },
    { title: '状态', dataIndex: 'status_name', width: 100 },
    { title: '操作', dataIndex: 'action', width: 80, render: v => v === 'update' ? '更新' : '新增' }
  ];

  return (
    <div>
      <h3>批量导入订单</h3>

      <Card style={{ marginTop: 16 }}>
        <Alert
          message="导入说明"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>支持 .xlsx 和 .xls 格式</li>
              <li>第一行为表头，以下为数据行</li>
              <li>表头名称请参考下方字段映射</li>
              <li>完结状态列填写状态名称，系统自动匹配</li>
              <li>主体列填写主体名称，系统自动匹配</li>
              <li>初始粉丝数和涨粉后粉丝数存在时，自动计算涨粉量</li>
              <li><b>编辑模式：</b>Excel中有ID列的行会更新已有订单，没有ID列的行会新增订单</li>
              <li><b>查重：</b>导入前可预览数据并检查重复抖音号</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Card title="字段映射" size="small" style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 13 }}>
            <span style={{ color: '#1677ff' }}>ID → id（编辑时保留）</span>
            <span>筛选使用 → filter_use</span>
            <span style={{ color: '#52c41a' }}>主体 → supplier（按名称匹配）</span>
            <span>平台 → platform</span>
            <span>受理员工 → handler</span>
            <span>小组 → team</span>
            <span>期数 → period</span>
            <span>群名 → group_name</span>
            <span>下单时间 → order_time</span>
            <span>对接人 → contact_person</span>
            <span>涨粉方式 → growth_method</span>
            <span>视频号名称/抖音号 → account_name</span>
            <span>链接属性 → link_type</span>
            <span>专属链接/涨粉链接 → exclusive_link</span>
            <span>账号重复 → account_duplicate</span>
            <span>方式+账号重复 → method_duplicate</span>
            <span>初始粉丝数 → initial_fans</span>
            <span>涨粉后粉丝数 → final_fans</span>
            <span>完结状态 → status_name</span>
            <span>完成日期 → complete_date</span>
            <span>参考价格 → reference_price</span>
            <span>实际价格 → actual_price</span>
            <span>备注 → remark</span>
            <span>涨粉前账号截图文字 → pre_growth_text</span>
            <span>涨粉完结截图文字 → post_growth_text</span>
          </div>
        </Card>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              setFileList([file]);
              setPreviewData(null);
              setDuplicateData([]);
              setResult(null);
              return false;
            }}
            fileList={fileList}
            onRemove={() => { setFileList([]); setPreviewData(null); setDuplicateData([]); setResult(null); }}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>选择Excel文件</Button>
          </Upload>

          <div>
            <span style={{ marginRight: 12 }}>查重方式：</span>
            <Radio.Group value={checkType} onChange={e => setCheckType(e.target.value)}>
              <Radio value="account">按账号查重</Radio>
              <Radio value="account_method">按账号+涨粉方式查重</Radio>
            </Radio.Group>
          </div>

          <Space>
            <Button
              onClick={handlePreview}
              loading={checking}
              disabled={fileList.length === 0}
              size="large"
            >
              预览并查重
            </Button>
            {previewData && previewData.duplicates && previewData.duplicates.length > 0 ? (
              <>
                <Button
                  onClick={() => handleImport(true)}
                  loading={importing}
                  size="large"
                >
                  去除重复后添加
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleImport(false)}
                  loading={importing}
                  size="large"
                >
                  全部添加（含重复）
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                onClick={() => handleImport(false)}
                loading={importing}
                disabled={fileList.length === 0}
                size="large"
              >
                开始导入
              </Button>
            )}
            <Button onClick={() => navigate('/orders')}>返回列表</Button>
          </Space>
        </Space>
      </Card>

      {/* 预览结果 */}
      {previewData && (
        <Card title="数据预览" style={{ marginTop: 16 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <span>总行数: {previewData.total}</span>
            <span style={{ color: '#1677ff' }}>新增: {previewData.insert_count} 条</span>
            <span style={{ color: '#faad14' }}>更新: {previewData.update_count} 条</span>
            {previewData.duplicates && previewData.duplicates.length > 0 && (
              <span style={{ color: '#ff4d4f' }}>重复: {previewData.duplicates.length} 条</span>
            )}
          </Space>

          {previewData.rows && previewData.rows.length > 0 && (
            <Table
              columns={previewColumns}
              dataSource={previewData.rows.map((r, i) => ({ ...r, key: i }))}
              pagination={{ pageSize: 20 }}
              size="small"
              bordered
            />
          )}
        </Card>
      )}

      {/* 重复数据 */}
      {previewData && previewData.duplicates && previewData.duplicates.length > 0 && (
        <Card title="重复数据" style={{ marginTop: 16 }}>
          <Alert
            message="发现以下重复数据"
            description="有ID的行将更新已有订单，导入时会自动处理"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={duplicateColumns}
            dataSource={previewData.duplicates.map((d, i) => ({ ...d, key: i }))}
            pagination={false}
            size="small"
            bordered
          />
        </Card>
      )}

      {/* 导入结果 */}
      {result && (
        <Card title="导入结果" style={{ marginTop: 16 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <span style={{ color: '#52c41a', fontSize: 16 }}>
              <CheckCircleOutlined /> 成功: {result.success_count} 条
            </span>
            {result.skip_count > 0 && (
              <span style={{ color: '#faad14', fontSize: 16 }}>
                跳过重复: {result.skip_count} 条
              </span>
            )}
            {result.error_count > 0 && (
              <span style={{ color: '#ff4d4f', fontSize: 16 }}>
                <CloseCircleOutlined /> 失败: {result.error_count} 条
              </span>
            )}
          </Space>

          {result.errors && result.errors.length > 0 && (
            <Table
              columns={errorColumns}
              dataSource={result.errors.map((e, i) => ({ ...e, key: i }))}
              pagination={false}
              size="small"
              bordered
            />
          )}

          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate('/orders')}>
              查看订单列表
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default OrderImport;
